import React, { useState } from 'react';
import { Box, Button, Stack, Typography, Paper, IconButton, CircularProgress, Divider, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { HttpService } from 'core/services/http';
import { CVHttpService } from 'features/feature_cv/services';
import { Utils } from 'core/utils';
import LanguageFormDialog from 'features/feature_cv/components/dialogs/LanguageFormDialog';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

function LanguagesSection({ cvId }) {
    const queryClient = useQueryClient();
    const [openDialog, setOpenDialog] = useState(false);
    const [editingLanguage, setEditingLanguage] = useState(null);

    const { data, isLoading, refetch } = useQuery(
        ['cv_languages', cvId],
        async () => {
            const service = new HttpService();
            const response = await service.get(`/job/cv/userlanguage/?cv=${cvId}`, null);
            return Array.isArray(response) ? response : (response?.results || []);
        },
        { enabled: !!cvId }
    );

    const deleteMutation = useMutation(
        (id) => CVHttpService.deleteLanguage(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['cv_languages', cvId]);
            }
        }
    );

    const languages = data || [];

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this language?')) return;
        deleteMutation.mutate(id);
    };

    const handleSave = async () => {
        await refetch();
        setOpenDialog(false);
        setEditingLanguage(null);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                    Languages ({languages.length})
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingLanguage(null);
                        setOpenDialog(true);
                    }}
                >
                    Add Language
                </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {languages.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No languages added yet.
                </Typography>
            ) : (
                <Stack spacing={2}>
                    {languages.map((lang) => (
                        <Paper key={lang.id} variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                <Box sx={{ flexGrow: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {formatText(lang.title)}
                                        </Typography>
                                        {lang.proficiency && (
                                            <Chip label={lang.proficiency} size="small" variant="outlined" />
                                        )}
                                    </Stack>
                                </Box>
                                <Stack direction="row" spacing={0.5}>
                                    <IconButton color="primary"
                                        size="small"
                                        onClick={() => {
                                            setEditingLanguage(lang);
                                            setOpenDialog(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(lang.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <LanguageFormDialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setEditingLanguage(null);
                }}
                onSave={handleSave}
                cvId={cvId}
                language={editingLanguage}
            />
        </Paper>
    );
}

export default LanguagesSection;

